import { useCallback, useMemo, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { ClassNames } from "@emotion/react";
import styled from "@emotion/styled";
import { Input } from "antd5";
import { isEmpty } from "lodash";

import FilterDrawer from "components/filter_form/filter_drawer/FilterDrawer";
import Button from "components/ui/button/Button";
import { LEADS_KANBAN, useVariableValue } from "lib/featureFlags/generated/dvcVariableTypes";
import { useTeams } from "lib/hooks/api/teams/useTeams";
import { usePersistedTableView } from "lib/hooks/usePersistedTableView";
import { useTablePageSize } from "lib/hooks/useTablePageSize";
import { parsePaginationUrlState, useURLState } from "lib/hooks/useURLState";
import { useIsLaptopUp } from "lib/providers/Responsive";
import { LeadSortBy, LeadSortOrder } from "lib/types/graphQLEnums";
import EditColumnsButton from "../../../lib/core_components/EditColumnsButton";
import { useDebouncedValue } from "../../../lib/debounce";
import { PaginationOptions } from "../../../lib/generated/app-service-gql/graphql";
import { useLeads } from "../../../lib/hooks/api/leads/useLeads";
import { EventDataTypes } from "../../../lib/tracking";
import { ColumnSetting, TableSettings } from "../../../lib/types/models";
import { Flex } from "../../../styles/utility-components";
import { TeamDropdownFilter } from "../../form_components/dropdown_list_selects/TeamDropdownFilter";
import { UserDropdownFilter } from "../../form_components/dropdown_list_selects/UserDropdownFilter";
import { clearBuyerFilters } from "../../form_components/utils";
import { LeadsBoard } from "../leads_board/LeadsBoard";
import { LeadsViewMode, LeadsViewModeSchema, ViewToggle } from "../leads_board/ViewToggle";
import ExportLeadsButton from "./ExportLeadsButton";
import { FilterForm } from "./FilterForm";
import LeadsActionBar from "./LeadActionBar";
import { LeadsTable, SortState } from "./LeadsTable";
import { QuickFilterBar } from "./QuickFilterBar";
import {
  ALL_AVAILABLE_LEAD_COLUMNS,
  convertLeadFiltersToSearchRequest,
  DEFAULT_VISIBLE_LEAD_COLUMNS,
  EMPTY_LEAD_FILTERS,
  LeadColumns,
  LeadFilters,
  LeadFiltersSchema,
  mapSortFieldToEnum,
  mapSortOrderToEnum,
} from "./utils";

type Props = {
  allAvailableColumns?: ColumnSetting<LeadColumns>[];
  defaultVisibleColumns?: ColumnSetting<LeadColumns>[];
  defaultFilters?: LeadFilters;
};

export function FilterableLeads({
  allAvailableColumns = ALL_AVAILABLE_LEAD_COLUMNS,
  defaultVisibleColumns = DEFAULT_VISIBLE_LEAD_COLUMNS,
  defaultFilters = EMPTY_LEAD_FILTERS,
}: Props) {
  const { data: teams } = useTeams();

  const { appliedColumns, setColumns, storedSort, setSort } = usePersistedTableView<
    LeadColumns,
    Pick<LeadFilters, "orderBy" | "order">
  >("leadsTableView", allAvailableColumns, defaultVisibleColumns);

  const effectiveDefaultFilters = useMemo(
    () => (storedSort ? { ...defaultFilters, ...storedSort } : defaultFilters),
    [storedSort, defaultFilters],
  );

  const [leadFilters = effectiveDefaultFilters, setLeadFilters] = useURLState(
    "leadFilters",
    effectiveDefaultFilters,
    LeadFiltersSchema,
    { encoder: "compress" },
  );
  const [pageSize, setPageSize] = useTablePageSize("leads-table");
  const [pagination = { page: 1, pageSize }, setPagination] = useURLState<PaginationOptions>(
    "pagination",
    { page: 1, pageSize },
    parsePaginationUrlState,
  );
  const [debouncedFilters] = useDebouncedValue(leadFilters, 500);
  const [debouncedPagination, updateDebouncedPagination] = useDebouncedValue(pagination, 500);
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);
  const [viewMode, setViewMode] = useURLState("leadsView", "table", LeadsViewModeSchema);

  // Below 1280 the row cannot hold the labelled quick filters, so they give way to All
  // Filters, which carries the same assignee and team fields.
  const showQuickFilters = useIsLaptopUp();

  const isBoardEnabled = useVariableValue(LEADS_KANBAN, false);
  // Gate the URL state too, not just the toggle — otherwise a shared
  // ?leadsView=board link would render the board for anyone.
  const effectiveViewMode: LeadsViewMode = isBoardEnabled ? viewMode : "table";

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  const setFilters = useCallback(
    (val: LeadFilters) => {
      setLeadFilters(val);
      setPagination({ page: 1, pageSize });
    },
    [setLeadFilters, setPagination, pageSize],
  );

  // The board has no checkboxes, so a selection carried over from the table would
  // sit invisibly behind the bulk action bar's Delete.
  const handleViewModeChange = useCallback(
    (value: LeadsViewMode) => {
      setViewMode(value);
      setSelectedLeadIds([]);
    },
    [setViewMode],
  );

  const handleSortChange = useCallback(
    (newSort: SortState) => {
      const sort = {
        orderBy: mapSortFieldToEnum(newSort.field),
        order: mapSortOrderToEnum(newSort.order),
      };
      setFilters({ ...leadFilters, ...sort });
      setSort(sort);
    },
    [leadFilters, setFilters, setSort],
  );

  const request = useMemo(
    () => convertLeadFiltersToSearchRequest(debouncedFilters),
    [debouncedFilters],
  );

  const { data, isLoading, isError } = useLeads(request, debouncedPagination, {
    enabled: !isEmpty(debouncedFilters),
  });

  return (
    <Wrapper column gap={16}>
      {/* One flat row on purpose. Grouping the search and filters in a nested Flex made that
          group size itself to the search input's 160px minimum rather than its 350px, so the
          filters wrapped below while the row still had hundreds of pixels spare. */}
      <Flex gap={8} alignItems="center" flexWrap="wrap" aria-label="Lead action items">
        <ClassNames>
          {({ css }) => (
            <Input
              placeholder="Search"
              prefix={<SearchOutlined />}
              value={leadFilters?.searchText ?? ""}
              onChange={(e) => {
                setFilters({ ...leadFilters, searchText: e.target.value });
              }}
              allowClear
              className={css({
                // antd Input is width:100%, which as a flex item claims the whole
                // row as its basis and squeezes the filter buttons. Give it an
                // explicit basis so it absorbs the shrinking instead.
                flex: "1 1 240px",
                minWidth: 160,
                maxWidth: 350,
              })}
            />
          )}
        </ClassNames>
        {/* The filter controls hold their width so the search input is what shrinks;
            without this they are squeezed and their labels truncate too early. */}
        <Flex gap={8} alignItems="center" flexShrink={0}>
          {showQuickFilters && (
            <>
              <UserDropdownFilter
                value={
                  leadFilters?.assignee ? leadFilters.assignee : { ids: [], includeNulls: false }
                }
                onChange={(value) => setFilters({ ...leadFilters, assignee: value })}
                options={{ noun: "assignee" }}
              />
              {teams && teams.length > 1 && (
                <TeamDropdownFilter
                  teamIds={leadFilters.teamIds ?? []}
                  onChange={(teamIds) => setFilters({ ...leadFilters, teamIds: teamIds })}
                />
              )}
            </>
          )}
          {/* Drops to the icon alone once the quick filters are gone, so the row still fits.
              The label is the accessible name either way. */}
          <Button
            as="button"
            buttonType="secondary"
            onClick={() => setIsFilterDrawerOpen(true)}
            icon="filterFunnel02"
            aria-label="All filters"
          >
            {showQuickFilters ? "All filters" : undefined}
          </Button>
        </Flex>
        {isBoardEnabled && (
          <RowActions>
            <ViewToggle value={viewMode} onChange={handleViewModeChange} />
          </RowActions>
        )}
      </Flex>
      <Flex gap={8} alignItems="center" flexWrap="wrap">
        <Flex gap={8} alignItems="center">
          <QuickFilterBar
            filters={leadFilters}
            isLoading={isLoading}
            totalResults={data?.total ?? 0}
            valueSum={data?.valueSum ?? 0}
            onClearFilter={(key) => setFilters({ ...leadFilters, ...clearBuyerFilters(key) })}
            onFilterClick={() => setIsFilterDrawerOpen(true)}
            clearAllFilters={() =>
              setFilters({
                ...EMPTY_LEAD_FILTERS,
                searchText: leadFilters.searchText === "" ? undefined : leadFilters.searchText,
              })
            }
          />
        </Flex>
        {/* Holds its width and stays right-aligned, whether it shares the line with the
            filter summary or wraps onto its own below it. */}
        <RowActions gap={8} flexShrink={0}>
          {/* Column visibility has no meaning on the board — the card decides its own rows. */}
          {effectiveViewMode !== "board" && (
            <EditColumnsButton<TableSettings<LeadColumns>>
              allAvailableColumns={[...allAvailableColumns]}
              selectedColumns={appliedColumns}
              onNewColumnSettingChange={(c) => {
                setColumns([...c]);
              }}
              dataType={EventDataTypes.lead}
            />
          )}
          <ExportLeadsButton
            filters={debouncedFilters}
            selectedLeads={selectedLeadIds}
            totalCount={data?.total ?? 0}
            isLoading={isLoading}
          />
        </RowActions>
      </Flex>
      {selectedLeadIds.length > 0 && (
        <LeadsActionBar
          selectedLeadIds={selectedLeadIds}
          onClearSelection={() => setSelectedLeadIds([])}
        />
      )}
      {effectiveViewMode === "board" ? (
        <LeadsBoard request={request} />
      ) : (
        <LeadsTable
          leads={data?.results}
          columnSettings={appliedColumns.map((c) => c.field)}
          isLoading={isLoading}
          isError={isError}
          sort={{
            field: leadFilters?.orderBy || LeadSortBy.UpdatedAt,
            order: leadFilters?.order || LeadSortOrder.Desc,
          }}
          onSortChange={handleSortChange}
          onSelectionChange={setSelectedLeadIds}
          selectedRowKeys={selectedLeadIds}
          onClearFilters={() => setFilters(EMPTY_LEAD_FILTERS)}
          pagination={{
            current: pagination.page ?? 1,
            pageSize: pagination.pageSize ?? 10,
            total: data?.total ?? 0,
            onChange: (page, pageSize) => {
              setPageSize(pageSize);
              setPagination({ page, pageSize });
              updateDebouncedPagination({ page, pageSize });
            },
            showSizeChanger: true,
            align: "center",
          }}
        />
      )}
      <FilterDrawer open={isFilterDrawerOpen} onClose={() => setIsFilterDrawerOpen(false)}>
        {isFilterDrawerOpen && (
          <FilterForm
            onClose={() => setIsFilterDrawerOpen(false)}
            onChange={setFilters}
            filters={leadFilters ?? EMPTY_LEAD_FILTERS}
            hiddenFilters={teams && teams.length > 1 ? [] : ["team"]}
          />
        )}
      </FilterDrawer>
    </Wrapper>
  );
}

const Wrapper = styled(Flex)({
  paddingBottom: 24,
  width: "100%",
});

// margin-left rather than the row's justify-content, so the actions stay right-aligned
// even once they have wrapped onto a line of their own.
const RowActions = styled(Flex)({
  marginLeft: "auto",
});
